import {
  type ExtractDocumentTypeFromTypedRxJsonSchema,
  type RxCollection,
  type RxJsonSchema,
  toTypedRxJsonSchema,
} from "rxdb";
import { useRxCollection, type useRxData } from "rxdb-hooks";
import { SYNCABLE_META } from "./common";

export const RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME = "epocus-encounter";

export const useRxCollectionEpocusEncounter =
  (): RxCollection<EpocusEncounter> | null =>
    useRxCollection<EpocusEncounter>(RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME);

type UseRxData = Parameters<typeof useRxData<EpocusEncounter>>;
export type EpocusEncounterQueryConstructor = UseRxData["1"];

export type EpocusEncounterStatus =
  | "imported"
  | "draft"
  | "pending_review"
  | "reviewed"
  | "complete";

const EPOCUS_ENCOUNTER_SCHEMA_LITERAL = {
  title: "epocus encounter schema",
  version: 11,
  description: "Represents a POCUS scanning session encounter",
  type: "object",
  primaryKey: "id",
  properties: {
    id: {
      type: "string",
      maxLength: 100,
    },
    elephantPatientId: {
      type: ["string", "null"],
      maxLength: 100,
    },
    patientName: {
      type: ["string", "null"],
    },
    // Demographics + operator prefilled from the GE DICOM payload.
    // patientDob is an ISO date string; operatorName is the free-text DICOM
    // OperatorsName tag (distinct from performerId, the matched clinician).
    patientDob: {
      type: ["string", "null"],
    },
    patientGender: {
      type: ["string", "null"],
    },
    operatorName: {
      type: ["string", "null"],
    },
    accessionNumber: {
      type: ["string", "null"],
    },
    dicomStudyId: {
      type: ["string", "null"],
    },
    status: {
      type: "string",
      maxLength: 20,
      enum: ["imported", "draft", "pending_review", "reviewed", "complete"],
    },
    performerId: {
      type: ["string", "null"],
      maxLength: 256,
    },
    programId: {
      type: ["string", "null"],
      maxLength: 100,
    },
    deviceId: {
      type: ["string", "null"],
    },
    // Server-owned: pulled from the DICOM payload and never pushed back. Drives
    // claim-pool matching against the performer's probe associations.
    deviceSerialNumber: {
      type: ["string", "null"],
    },
    location: {
      type: ["string", "null"],
    },
    date: {
      type: "string",
      format: "date-time",
      maxLength: 30,
    },
    meta: SYNCABLE_META,
  },
  required: ["id", "status", "date", "meta"],
  indexes: [
    // Supports the triage-queue query { selector: { status, performerId }, sort: date }.
    // The Mango selector includes performerId: null but RxDB uses this index for the
    // status predicate and date ordering; the performerId condition is evaluated against
    // the already-indexed result set, which is negligible with limit: 50.
    ["status", "date"],
  ],

  /*
   * It is important to set 'as const' to preserve the literal type.
   *
   * biome-ignore lint/suspicious/noExplicitAny: We infer the DB type from this
   * object, the satisfies just provides us type inference for this object.
   */
} as const satisfies RxJsonSchema<any>;

const schemaTyped = toTypedRxJsonSchema(EPOCUS_ENCOUNTER_SCHEMA_LITERAL);

export type EpocusEncounter = ExtractDocumentTypeFromTypedRxJsonSchema<
  typeof schemaTyped
>;

export const EPOCUS_ENCOUNTER_SCHEMA: RxJsonSchema<EpocusEncounter> =
  EPOCUS_ENCOUNTER_SCHEMA_LITERAL;
